<!--
  - Copyright (c) 2022 by Leyton Group - All Rights Reserved.
  - This source code, and resulting software, is the confidential and proprietary information
  - ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  - of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  - and shall use it only in accordance with the terms and conditions of any and all license
  - agreements you have entered into with The Company.
  -->

<template>
  <b-modal
    :id="id"
    modal-class="giga-modal medium-modal"
    content-class="text-center pb-2"
    no-close-on-esc
    no-close-on-backdrop
    hide-header
    hide-footer
    lazy
  >
    <div class="gigawhat-modal">
      <div class="modal-heading">
        <h2 class="modal-title" v-text="title"></h2>
      </div>
      <ul class="gigwhat-navTabs">
        <li
          class="navTab_item"
          :class="[
            { 'active-navTab': currentTab == tab.id },
            { 'done-stepTab': tab.id < currentTab }
          ]"
          v-for="(tab, index) in tabs"
          :key="index"
        >
          <button @click="tabNavigation(tab.id)">
            <span v-if="tab.id < currentTab" class="navTab-item_done">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10.782"
                height="7.769"
                viewBox="0 0 10.782 7.769"
              >
                <path
                  id="Tracé_2381"
                  data-name="Tracé 2381"
                  d="M67.246,104.023a1.113,1.113,0,0,0,.79-.327l5.534-5.534a1.117,1.117,0,0,0-1.58-1.58l-4.744,4.744L65.023,99.1a1.117,1.117,0,1,0-1.58,1.58l3.013,3.013a1.114,1.114,0,0,0,.79.327Z"
                  transform="translate(-63.115 -96.254)"
                  fill="#808fa3"
                />
              </svg>
            </span>
            <span v-else class="navTab-item_number">{{ tab.id }}</span>
            {{ tab.label }}
          </button>
        </li>
      </ul>
      <div class="body-modal">
        <Transition name="fade">
          <edit-estimation-steper
            :$v="$v"
            :addressValidator="$v.tabsAddress.$each[0]"
            :beneficiary="beneficiary"
            :ficheValidator="$v.tabsFiche.$each[0]"
            :currentTab="currentTab"
            :isVIEWER="isVIEWER"
            :isAnArtisan="isAnArtisan"
          ></edit-estimation-steper>
        </Transition>
      </div>
      <div class="gigwhat-model_footer mt-5">
        <div class="half-footer left-content">
          <div class="giga-button">
            <button
              v-if="currentTab > 1"
              :disabled="this.$v.$invalid"
              class="giga-blue_outlined_button no-icon"
              @click="goBack()"
            >
              Précédent
            </button>
          </div>
          <div class="giga-button">
            <button
              v-if="currentTab === 1"
              class="giga-blue_outlined_button no-icon"
              @click="$emit('closeClick')"
            >
              Quitter
            </button>
          </div>
        </div>
        <div class="half-footer right-content">
          <div class="giga-button">
            <button
              v-if="currentTab < tabs.length"
              :disabled="this.$v.$invalid"
              class="giga-green_button no-icon"
              @click="goNext()"
            >
              Suivant
            </button>
            <button
              v-if="currentTab === tabs.length"
              :disabled="this.$v.$invalid"
              class="giga-green_button no-icon"
              @click="saveEstimation()"
            >
              Terminer
            </button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { required, requiredIf, email } from "vuelidate/lib/validators";
import { constants } from "@/shared/constants";
import { simulationService } from "@/views/Simulations/services";
import EditEstimationSteper from "@/views/Simulations/import-mass-estimations/edit-estimation/EditEstimationSteper.vue";
import addressValidation from "@/views/Simulations/validation/address-validation";
import ficheValidation from "@/views/Simulations/validation/fiche-validation";
import { helpers } from "@/shared/helper";

export default {
  name: "EditEstimationModal",
  props: [
    "id",
    "title",
    "loader",
    "isReadOnly",
    "tabsAddress",
    "beneficiary",
    "tabsFiche"
  ],
  components: { EditEstimationSteper },
  data() {
    return {
      tabs: [
        {
          id: 1,
          label: "Bénéficiaire"
        },
        {
          id: 2,
          label: "Fiche"
        }
      ]
    };
  },
  validations: {
    tabsAddress: addressValidation,
    tabsFiche: ficheValidation,
    beneficiary: {
      isMoral: { required },
      physical: {
        id: {},
        email: {
          required: requiredIf(function() {
            return this.isAnArtisan && !this.$v.beneficiary.isMoral.$model;
          }),
          email
        },
        lastName: {
          required: requiredIf(function() {
            return this.isAnArtisan && !this.$v.beneficiary.isMoral.$model;
          })
        },
        name: {
          required: requiredIf(function() {
            return this.isAnArtisan && !this.$v.beneficiary.isMoral.$model;
          })
        },
        natureOccupation: {
          required: requiredIf(function() {
            return this.isAnArtisan && !this.$v.beneficiary.isMoral.$model;
          })
        },
        tele1: {
          required: requiredIf(function() {
            return this.isAnArtisan && !this.$v.beneficiary.isMoral.$model;
          }),
          phone: constants.phonePattern
        },
        tele2: {}
      },
      moral: {
        id: {},
        addressName: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          })
        },
        codeCommuneInsee: {},
        codePostal: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          })
        },
        libelleAcheminement: {},
        nomCommune: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          })
        },
        personneMoralEmail: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          }),
          email
        },
        personneMoralTelephone: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          }),
          phone: constants.phonePattern
        },
        raisonSociale: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          })
        },
        siren: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          }),
          pattern: constants.sirenInvalid
        },
        siret: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          }),
          pattern: constants.siretInvalid
        },
        type: {
          required: requiredIf(function() {
            return this.isAnArtisan && this.$v.beneficiary.isMoral.$model;
          })
        }
      }
    }
  },
  computed: {
    ...mapGetters("simulation", ["getSimulation", "isEdit"]),
    ...mapGetters("estimation", { currentTab: "getCurrentTab" }),
    isVIEWER() {
      return helpers.isVIEWER();
    },
    isAnArtisan() {
      return helpers.isAnArtisan();
    }
  },
  mounted() {
    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {});
  },
  methods: {
    ...mapMutations("estimation", { goTo: "goNext" }),
    goNext() {
      this.navigateTo(this.currentTab + 1);
    },
    goBack() {
      this.goTo(this.currentTab - 1);
    },
    tabNavigation(id) {
      let tab = this.tabs.findIndex(item => item.id == id);
      this.navigateTo(tab + 1);
    },
    navigateTo(id) {
      this.goTo(id);
      /*      if (!this.$v.userForm.$invalid) {
        this.goTo(id);
      } else {
        this.$v.userForm.$anyDirty = true;
      }*/
    },
    saveEstimation() {
      this.$emit("confirmClick");
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-title {
  text-align: center;
}
.body-modal {
  height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 25px;
}
</style>
